import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Box, Typography, Button, Stack, TextField, MenuItem, InputAdornment, Dialog, DialogContent, DialogActions,
    IconButton, Tooltip, Snackbar, Alert, Autocomplete, Skeleton,
} from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import CardGiftcardRoundedIcon from '@mui/icons-material/CardGiftcardRounded';
import http, { apiErrorMessage } from '../../Api/http';
import { GetPayrollIncentives, AddPayrollIncentive, UpdatePayrollIncentive, DeletePayrollIncentive, GetEmployees } from '../../Api/Api';
import { card, solidBtn, ghostBtn, dangerBtn, field, th, td, LoadAlert } from '../uiKit';
import { initialsFromName, cleanName, avatarTone } from '../../utils/format';

const inr = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;
const EMPTY = { id: null, employee: null, payoutMonth: '', amount: '', shootName: '', location: '', reason: '' };

// 12 payout months: 3 ahead of this month and 8 back.
const buildMonths = () => {
    const out = [];
    const now = new Date();
    for (let i = -3; i <= 8; i++) {
        const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
        out.push({ value: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`, label: d.toLocaleString('en-US', { month: 'long', year: 'numeric' }), isCurrent: i === 0 });
    }
    return out;
};

// One-off, ad-hoc amounts HR adds to an employee's pay for a payout month (a shoot bonus, a top-up).
// No approval step. Locked by the server once that month's payroll is Approved or Credited.
export default function PayrollIncentivesTab() {
    const months = useMemo(buildMonths, []);
    const [month, setMonth] = useState(months.find((m) => m.isCurrent).value);
    const [search, setSearch] = useState('');
    const [data, setData] = useState({ totalAmount: 0, count: 0, items: [] });
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState('');
    const [employees, setEmployees] = useState([]);
    const [form, setForm] = useState(null);
    const [saving, setSaving] = useState(false);
    const [confirmDel, setConfirmDel] = useState(null);
    const [snack, setSnack] = useState({ msg: '', severity: 'success' });

    const load = useCallback(async () => {
        setLoading(true); setLoadError('');
        try {
            const { data: body } = await http.get(GetPayrollIncentives, { params: { payoutMonth: month, search: search.trim() } });
            if (body?.error) throw new Error(body.message || 'Could not load incentives.');
            const d = body?.data || {};
            setData({ totalAmount: d.totalAmount || 0, count: d.count || 0, items: d.items || [] });
        } catch (err) {
            // An empty month comes back as 404: that is just an empty table.
            if (err?.response?.status === 404) setData({ totalAmount: 0, count: 0, items: [] });
            else { setLoadError(err?.response || err?.request ? apiErrorMessage(err, 'Could not load incentives.') : err.message); setData({ totalAmount: 0, count: 0, items: [] }); }
        } finally { setLoading(false); }
    }, [month, search]);

    useEffect(() => { const t = setTimeout(load, search ? 350 : 0); return () => clearTimeout(t); }, [load, search]);

    useEffect(() => {
        http.get(GetEmployees, { params: { search: '', status: 'active', page: 1, pageSize: 1000 } })
            .then(({ data: body }) => setEmployees((body?.data?.items || []).filter((e) => String(e.status || 'active').toLowerCase() === 'active')))
            .catch(() => { });
    }, []);

    const openAdd = () => setForm({ ...EMPTY, payoutMonth: month });
    const openEdit = (r) => setForm({
        id: r.id, payoutMonth: r.payoutMonth || month, amount: String(r.amount ?? ''), shootName: r.shootName || '', location: r.location || '', reason: r.reason || '',
        employee: employees.find((e) => e.employeeCode === r.employeeCode) || { employeeCode: r.employeeCode, fullName: r.employeeName },
    });
    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const save = async () => {
        if (!form || saving) return;
        const amount = Number(form.amount);
        if (!form.employee) { setSnack({ msg: 'Choose an employee.', severity: 'error' }); return; }
        if (!Number.isFinite(amount) || amount <= 0) { setSnack({ msg: 'Enter an amount greater than 0.', severity: 'error' }); return; }
        const payload = {
            employeeCode: form.employee.employeeCode, payoutMonth: form.payoutMonth, amount,
            shootName: form.shootName.trim(), location: form.location.trim(), reason: form.reason.trim(),
        };
        setSaving(true);
        try {
            const { data: body } = form.id ? await http.put(UpdatePayrollIncentive, { id: form.id, ...payload }) : await http.post(AddPayrollIncentive, payload);
            if (body?.error) throw new Error(body.message || 'Could not save the incentive.');
            setSnack({ msg: form.id ? 'Incentive updated.' : 'Incentive added. Run payroll again to include it.', severity: 'success' });
            setForm(null);
            if (form.payoutMonth !== month) setMonth(form.payoutMonth); else load();
        } catch (err) {
            setSnack({ msg: err?.response || err?.request ? apiErrorMessage(err, 'Could not save the incentive.') : err.message, severity: 'error' });
        } finally { setSaving(false); }
    };

    const remove = async () => {
        if (!confirmDel) return;
        setSaving(true);
        try {
            const { data: body } = await http.delete(DeletePayrollIncentive, { params: { id: confirmDel.id } });
            if (body?.error) throw new Error(body.message || 'Could not delete the incentive.');
            setSnack({ msg: 'Incentive removed.', severity: 'success' });
            setConfirmDel(null); load();
        } catch (err) {
            setSnack({ msg: err?.response || err?.request ? apiErrorMessage(err, 'Could not delete the incentive.') : err.message, severity: 'error' });
        } finally { setSaving(false); }
    };

    return (
        <Box sx={{ p: 2 }}>
            <Box sx={{ ...card, p: 0, overflow: 'hidden' }}>
                <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5, p: 2.2, bgcolor: '#F7F6FD', borderBottom: '1px solid #EAE7F7' }}>
                    <Stack direction="row" spacing={1.2} sx={{ alignItems: 'center' }}>
                        <Box sx={{ width: 28, height: 28, borderRadius: '8px', bgcolor: '#fff', boxShadow: '0 1px 4px rgba(16,24,40,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <CardGiftcardRoundedIcon sx={{ color: '#7C5CFC', fontSize: 18 }} />
                        </Box>
                        <Box>
                            <Typography sx={{ fontSize: 14, fontWeight: 800, color: '#0F172A' }}>Payroll Incentives</Typography>
                            <Typography sx={{ fontSize: 11.5, color: '#6B7280' }}>
                                {data.count} entr{data.count === 1 ? 'y' : 'ies'} · total <strong style={{ color: '#5B21B6' }}>{inr(data.totalAmount)}</strong>
                            </Typography>
                        </Box>
                    </Stack>
                    <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                        <TextField select size="small" value={month} onChange={(e) => setMonth(e.target.value)} sx={{ ...field, width: 210, '& .MuiOutlinedInput-root': { ...field['& .MuiOutlinedInput-root'], height: 38, bgcolor: '#fff' } }}>
                            {months.map((m) => <MenuItem key={m.value} value={m.value} sx={{ fontSize: 13 }}>{m.label}{m.isCurrent ? ' (current)' : ''}</MenuItem>)}
                        </TextField>
                        <TextField
                            size="small" placeholder="Search employee…" value={search} onChange={(e) => setSearch(e.target.value)}
                            sx={{ ...field, width: 220, '& .MuiOutlinedInput-root': { ...field['& .MuiOutlinedInput-root'], height: 38, bgcolor: '#fff', borderRadius: '7px' } }}
                            slotProps={{ input: { startAdornment: <InputAdornment position="start"><SearchRoundedIcon sx={{ fontSize: 18, color: '#9CA3AF' }} /></InputAdornment> } }}
                        />
                        <Button startIcon={<AddRoundedIcon />} onClick={openAdd} sx={{ ...solidBtn, height: 38, px: 2 }}>Add incentive</Button>
                    </Stack>
                </Stack>

                {loadError && <LoadAlert action={<Button onClick={load} sx={{ textTransform: 'none', fontWeight: 700 }}>Retry</Button>}>{loadError}</LoadAlert>}

                <Box sx={{ overflowX: 'auto' }}>
                    <Box component="table" sx={{ width: '100%', minWidth: 860, borderCollapse: 'collapse' }}>
                        <Box component="thead" sx={{ bgcolor: '#F4F3FB' }}>
                            <Box component="tr">
                                {['EMPLOYEE', 'SHOOT', 'LOCATION', 'REASON', 'AMOUNT', 'ADDED', ''].map((h, i) => (
                                    <Box component="th" key={i} sx={{ ...th, textAlign: h === 'AMOUNT' ? 'right' : 'left' }}>{h}</Box>
                                ))}
                            </Box>
                        </Box>
                        <Box component="tbody">
                            {loading && Array.from({ length: 4 }).map((_, i) => (
                                <Box component="tr" key={i}>{Array.from({ length: 7 }).map((__, j) => <Box component="td" key={j} sx={td}><Skeleton width={j === 0 ? 150 : 80} /></Box>)}</Box>
                            ))}
                            {!loading && data.items.map((r, idx) => (
                                <Box component="tr" key={r.id} sx={{ bgcolor: idx % 2 ? '#FBFAFE' : '#fff', '&:hover': { bgcolor: '#F5F4FC' } }}>
                                    <Box component="td" sx={td}>
                                        <Stack direction="row" spacing={1.2} sx={{ alignItems: 'center' }}>
                                            <Box sx={{ width: 32, height: 32, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, ...avatarTone(r.employeeName) }}>{initialsFromName(r.employeeName)}</Box>
                                            <Box>
                                                <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#0F172A' }}>{cleanName(r.employeeName)}</Typography>
                                                <Typography sx={{ fontSize: 11, color: '#98A0AE' }}>{r.employeeCode}</Typography>
                                            </Box>
                                        </Stack>
                                    </Box>
                                    <Box component="td" sx={{ ...td, fontSize: 13, color: '#475569' }}>{r.shootName || '—'}</Box>
                                    <Box component="td" sx={{ ...td, fontSize: 13, color: '#475569' }}>{r.location || '—'}</Box>
                                    <Box component="td" sx={{ ...td, fontSize: 13, color: '#475569', maxWidth: 260 }}>{r.reason || '—'}</Box>
                                    <Box component="td" sx={{ ...td, textAlign: 'right', fontSize: 13.5, fontWeight: 800, color: '#5B21B6' }}>{inr(r.amount)}</Box>
                                    <Box component="td" sx={{ ...td, fontSize: 11.5, color: '#98A0AE', whiteSpace: 'nowrap' }}>{r.createdOn || ''}{r.createdBy ? ` · ${r.createdBy}` : ''}</Box>
                                    <Box component="td" sx={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                                        <Tooltip arrow title="Edit"><IconButton size="small" onClick={() => openEdit(r)}><EditRoundedIcon sx={{ fontSize: 17 }} /></IconButton></Tooltip>
                                        <Tooltip arrow title="Delete"><IconButton size="small" onClick={() => setConfirmDel(r)} sx={{ color: '#E11D48' }}><DeleteOutlineRoundedIcon sx={{ fontSize: 17 }} /></IconButton></Tooltip>
                                    </Box>
                                </Box>
                            ))}
                        </Box>
                    </Box>
                    {!loading && !loadError && data.items.length === 0 && (
                        <Box sx={{ textAlign: 'center', py: 6 }}>
                            <CardGiftcardRoundedIcon sx={{ fontSize: 34, color: '#CBD2DD' }} />
                            <Typography sx={{ fontSize: 14, color: '#98A0AE', mt: 1 }}>{search ? 'No incentives match your search' : 'No incentives for this month'}</Typography>
                        </Box>
                    )}
                </Box>
            </Box>

            <Dialog open={Boolean(form)} onClose={() => !saving && setForm(null)} maxWidth="xs" fullWidth>
                {form && (
                    <>
                        <DialogContent sx={{ p: 2.5 }}>
                            <Typography sx={{ fontSize: 16, fontWeight: 800, color: '#0F172A', mb: 0.3 }}>{form.id ? 'Edit incentive' : 'Add incentive'}</Typography>
                            <Typography sx={{ fontSize: 12, color: '#6B7280', mb: 2 }}>A one-off amount for one payout month. It is not prorated and is not part of PF / ESI / PT.</Typography>
                            <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: '#334155', mb: 0.6 }}>Employee</Typography>
                            <Autocomplete
                                size="small" options={employees} value={form.employee} sx={{ mb: 1.4 }}
                                onChange={(_, v) => setForm((f) => ({ ...f, employee: v }))}
                                getOptionLabel={(o) => `${cleanName(o.fullName || '')} (${o.employeeCode})`}
                                isOptionEqualToValue={(a, b) => a.employeeCode === b.employeeCode}
                                renderInput={(p) => <TextField {...p} placeholder="Select employee" sx={field} />}
                            />
                            <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: '#334155', mb: 0.6 }}>Payout month</Typography>
                            <TextField select fullWidth size="small" value={form.payoutMonth} onChange={set('payoutMonth')} sx={{ ...field, mb: 1.4 }}>
                                {months.map((m) => <MenuItem key={m.value} value={m.value} sx={{ fontSize: 13 }}>{m.label}</MenuItem>)}
                            </TextField>
                            {[['amount', 'Amount', 'number'], ['shootName', 'Shoot name (optional)', 'text'], ['location', 'Location (optional)', 'text'], ['reason', 'Reason (optional)', 'text']].map(([k, label, type]) => (
                                <Box key={k} sx={{ mb: 1.4 }}>
                                    <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: '#334155', mb: 0.6 }}>{label}</Typography>
                                    <TextField
                                        fullWidth size="small" type={type} value={form[k]} onChange={set(k)} sx={field}
                                        slotProps={k === 'amount' ? { input: { startAdornment: <InputAdornment position="start">₹</InputAdornment> }, htmlInput: { min: 0 } } : undefined}
                                    />
                                </Box>
                            ))}
                        </DialogContent>
                        <DialogActions sx={{ px: 2.5, pb: 2 }}>
                            <Button onClick={() => setForm(null)} disabled={saving} sx={{ ...ghostBtn, height: 38 }}>Cancel</Button>
                            <Button onClick={save} disabled={saving} sx={{ ...solidBtn, height: 38, px: 2.2 }}>{saving ? 'Saving…' : (form.id ? 'Save' : 'Add incentive')}</Button>
                        </DialogActions>
                    </>
                )}
            </Dialog>

            <Dialog open={Boolean(confirmDel)} onClose={() => !saving && setConfirmDel(null)} maxWidth="xs" fullWidth>
                <DialogContent sx={{ p: 2.5 }}>
                    <Typography sx={{ fontSize: 16, fontWeight: 800, color: '#0F172A' }}>Remove this incentive?</Typography>
                    <Typography sx={{ fontSize: 13, color: '#6B7280', mt: 0.8 }}>
                        {confirmDel && `${inr(confirmDel.amount)} for ${cleanName(confirmDel.employeeName)}. It will no longer be added to their pay.`}
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 2.5, pb: 2 }}>
                    <Button onClick={() => setConfirmDel(null)} disabled={saving} sx={{ ...ghostBtn, height: 38 }}>Cancel</Button>
                    <Button onClick={remove} disabled={saving} sx={{ ...dangerBtn, height: 38, px: 2 }}>{saving ? 'Removing…' : 'Remove'}</Button>
                </DialogActions>
            </Dialog>

            <Snackbar open={Boolean(snack.msg)} autoHideDuration={5000} onClose={() => setSnack((s) => ({ ...s, msg: '' }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
                <Alert onClose={() => setSnack((s) => ({ ...s, msg: '' }))} severity={snack.severity} variant="filled" sx={{ borderRadius: '7px' }}>{snack.msg}</Alert>
            </Snackbar>
        </Box>
    );
}
